import React, { useState, useContext, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';

import values from 'lodash/values';
import forEach from 'lodash/forEach';
import isArray from 'lodash/isArray';
import isPlainObject from 'lodash/isPlainObject';

import {
    Grid,
    Box,
    Button,
    Dialog,
    DialogTitle,
    DialogActions,
    Typography,
    DialogContent,
    Collapse,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EventGeneralDetailsForm from './EventGeneralDetailsForm';
import EventAccessDetailsForm from './EventAccessDetailsForm';
import EventRewardsForm from './EventRewardsForm';
import EventRoundDetailsForm from './EventRoundDetailsForm';
import { DataContext } from '../data/DataContext';
import { DataContextType } from '../data_types/interfaces';
import { createDefaultEventFormData, EventAccess, EventBanner, EventFormData, EventRound, PlainObject } from '../data_types/types';
import { REWARD_OPTIONS } from '../data/disc_data';
import { SelectChangeEvent } from '@mui/material/Select';
import { gridSpacing } from '../../assets/theme/muitheme';
import { DS, Meter, Overline, PageHeader, enterSx, numericSx } from '../shared/Dashboard';

export const VALID_FIELD = false;

type SectionKey = 'general' | 'access' | 'rewards' | 'rounds';

interface SectionProps {
    sectionKey: SectionKey;
    step: number;
    title: string;
    subtitle: string;
    complete: boolean;
    expanded: boolean;
    onToggle: (key: SectionKey) => void;
    delay: number;
    children: React.ReactNode;
}

const FormSection: React.FC<SectionProps> = ({ sectionKey, step, title, subtitle, complete, expanded, onToggle, delay, children }) => (
    <Box id={`event-section-${sectionKey}`} sx={{
        background: DS.surface,
        border: `1px solid ${expanded ? DS.borderStrong : DS.border}`,
        borderRadius: DS.radius,
        overflow: 'hidden',
        scrollMarginTop: '16px',
        transition: 'border-color 0.2s ease',
        ...enterSx(delay),
    }}>
        <Box
            role="button"
            aria-expanded={expanded}
            onClick={() => onToggle(sectionKey)}
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                px: { xs: 2, sm: 3 },
                py: 2,
                cursor: 'pointer',
                '&:hover': { background: DS.inset },
            }}
        >
            <Box sx={{
                width: 28,
                height: 28,
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '50%',
                fontSize: '13px',
                fontWeight: 800,
                background: complete ? alpha(DS.brand, 0.16) : DS.inset,
                color: complete ? DS.brand : DS.textMuted,
                border: `1px solid ${complete ? alpha(DS.brand, 0.35) : DS.border}`,
            }}>
                {complete ? <CheckCircleIcon sx={{ fontSize: 16 }} /> : step}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 700, fontSize: '16px', color: DS.text }}>{title}</Typography>
                <Typography sx={{ fontSize: '13px', color: DS.textMuted }}>{subtitle}</Typography>
            </Box>
            <ExpandMoreIcon sx={{
                color: DS.textMuted,
                transition: 'transform 0.2s ease',
                transform: expanded ? 'rotate(180deg)' : 'none',
            }} />
        </Box>
        <Collapse in={expanded}>
            <Box sx={{ px: { xs: 2, sm: 3 }, pt: 1, pb: 3 }}>
                {children}
            </Box>
        </Collapse>
    </Box>
);

const SummaryRow: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 0.75 }}>
        <Typography sx={{ fontSize: '13px', color: DS.textMuted, flexShrink: 0 }}>{label}</Typography>
        <Typography component="div" sx={{ fontSize: '13px', color: DS.text, fontWeight: 600, textAlign: 'right', minWidth: 0, wordBreak: 'break-word', ...numericSx }}>
            {children}
        </Typography>
    </Box>
);

interface AddEventFormProps {
    eventFormData?: EventFormData;
    isEditMode: boolean;
}

const AddEventForm = (props: AddEventFormProps) => {
    const navigate = useNavigate();
    const context = useContext<DataContextType | undefined>(DataContext);

    const { userId, isAdmin, isCreator, addEventData, } = context || {};
    const { isEditMode, eventFormData } = props;

    const [formData, setFormData] = useState<EventFormData>(() => eventFormData || createDefaultEventFormData());
    const [isValid, setIsValid] = useState<boolean>(true);
    const [dialogOpen, setDialogOpen] = useState<boolean>(false);
    const [expandedSections, setExpandedSections] = useState<{[key: string]: boolean}>({
        general: true,
        access: false,
        rewards: false,
        rounds: false,
    });

    // Kept across renders so every section's latest errors are combined
    const formValidationData = useRef<PlainObject>({});

    const handleSectionToggle = (sectionKey: string) => {
        setExpandedSections(prev => ({
            ...prev,
            [sectionKey]: !prev[sectionKey],
        }));
    };

    const openSection = (sectionKey: SectionKey) => {
        setExpandedSections(prev => ({ ...prev, [sectionKey]: true }));
        setTimeout(() => {
            document.getElementById(`event-section-${sectionKey}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 50);
    };

    const generalComplete = !!(formData.eventID && formData.eventName && formData.eventStartTime && formData.eventEndTime);
    const configuredRounds = (formData.rounds || []).filter(round =>
        round.courseID || (round.customCourse && round.courseName)
    ).length;
    const rewardComplete = formData.participationReward !== undefined;

    const calculateProgress = () => {
        let completedSections = 0;
        const totalSections = 4; // Reduced from 5 to 4 after removing division

        // Check if general details are filled
        if (generalComplete) {
            completedSections++;
        }

        // Check access details (assuming they have defaults)
        completedSections++;

        // Check rewards (assuming they're optional)
        completedSections++;

        // Check rounds configuration
        if (configuredRounds > 0) {
            completedSections++;
        }

        return (completedSections / totalSections) * 100;
    };

    const isFormValid = useCallback((validationData: PlainObject): boolean => {
        var isValid = true;

        forEach(values(validationData), (value) => {
            if (isPlainObject(value)) {
                if (!isFormValid(values(value))) {
                    isValid = false;
                    return false;
                }

            } else if (isArray(value)) {
                if (!isFormValid(value)) {
                    isValid = false;
                    return false;
                }

            } else {
                if (value !== VALID_FIELD) {
                    isValid = false;
                    return false;
                }

            }
        });

        return isValid;
    }, []);

    const handleFormValidationChange = useCallback((errors: PlainObject) => {
        formValidationData.current = {
            ...formValidationData.current,
            ...errors
        };

        setIsValid(isFormValid(formValidationData.current));
    }, [isFormValid]);

    const handleInputChange = useCallback((event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent | { value: unknown }) => {
        if ('target' in event) {
            const target = event.target as HTMLInputElement;
            const { name, value } = target;

            setFormData((prevData) => ({
                ...prevData,
                [name]: value,
            }));
        } else {
            console.error('Event target not found');
        }
    }, []);

    const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, checked } = event.target;

        setFormData((prevData) => ({
            ...prevData,
            [name]: checked,
        }));
    };

    // Banner value to fall back to when the custom background is unticked (e.g. keeps PaidEvent)
    const initialBanner = useRef<number>(eventFormData?.banner ?? EventBanner.FirstRound);

    const handleCustomBackgroundChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const fallbackBanner = initialBanner.current === EventBanner.CustomBackground
            ? EventBanner.FirstRound
            : initialBanner.current;

        setFormData((prevData) => ({
            ...prevData,
            banner: event.target.checked ? EventBanner.CustomBackground : fallbackBanner,
        }));
    };

    const handleAccessChange = (eventAccess: EventAccess) => {
        setFormData((prevData) => ({ ...prevData, eventAccess }));
    };

    const handleInvitedPlayersChange = (invitedPlayers: string[]) => {
        setFormData((prevData) => ({ ...prevData, invitedPlayers }));
    };

    const handleRoundChange = (index: number, updatedRound: EventRound) => {
        const updatedRounds = [...formData.rounds];
        updatedRounds[index] = updatedRound;
        setFormData((prevData) => ({
            ...prevData,
            rounds: updatedRounds,
        }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        const eventId = formData.eventID;

        if (!isAdmin && !isCreator) {
            alert('You do not have permission to create events');
            return;
        }

        if (!eventId) {
            alert('Event ID is required.');
            return;
        }

        if (!isValid) {
            alert('This form has errors that need to be fixed before creating a new event');
            return;
        }

        try {
            if (addEventData) {
                // New events are owned by whoever creates them; edits keep the existing owner (or none)
                const eventData: EventFormData = !isEditMode && userId ? { ...formData, createdBy: userId } : { ...formData };
                // The invite list only means something for invite-only events (and Firebase rejects undefined)
                if (eventData.eventAccess !== EventAccess.Invite || !eventData.invitedPlayers?.length) {
                    delete eventData.invitedPlayers;
                }
                await addEventData(eventId, eventData);
                setDialogOpen(true); // Open the dialog
            } else {
                console.error('addEventData is undefined');
            }
        } catch (error) {
            console.error('Error adding event:', error);
            alert('An error occurred while adding the event.');
        }
    };

    const handleDialogClose = () => {
        setDialogOpen(false);
        window.location.replace('/admin/events'); // Redirect after closing the dialog
    };

    // Summary helpers (event times are UTC)
    const start = formData.eventStartTime ? moment.utc(formData.eventStartTime) : undefined;
    const end = formData.eventEndTime ? moment.utc(formData.eventEndTime) : undefined;
    const durationLabel = start?.isValid() && end?.isValid() && end.isAfter(start)
        ? moment.duration(end.diff(start)).humanize()
        : '—';
    const rewardLabel = REWARD_OPTIONS.find((option) => option.value === formData.participationReward)?.label ?? '—';
    const totalRounds = (formData.rounds || []).length;
    const progress = calculateProgress();
    const isInvite = formData.eventAccess === EventAccess.Invite;
    const invitedCount = (formData.invitedPlayers || []).length;
    const accessLabel = isInvite ? `Invite only · ${invitedCount} ${invitedCount === 1 ? 'player' : 'players'}` : 'Standard';

    const checklist: { key: SectionKey; label: string; done: boolean; detail: string }[] = [
        { key: 'general', label: 'Event details', done: generalComplete, detail: generalComplete ? 'ID, name and schedule set' : 'ID, name, start and end' },
        { key: 'access', label: 'Access', done: true, detail: accessLabel },
        { key: 'rewards', label: 'Rewards', done: rewardComplete, detail: rewardLabel },
        { key: 'rounds', label: 'Rounds', done: totalRounds > 0 && configuredRounds === totalRounds, detail: `${configuredRounds} of ${totalRounds} courses picked` },
    ];

    return (
        <Box component="form" onSubmit={handleSubmit}>
            <Grid container spacing={gridSpacing} sx={{ mb: 6 }}>
                <PageHeader
                    overline="Admin · Events"
                    title={isEditMode ? 'Edit event' : 'Create new event'}
                    subtitle={isEditMode ? 'Modify event settings and configuration' : 'Set up your disc golf event with detailed configuration'}
                    action={
                        <Button
                            startIcon={<ArrowBackIcon />}
                            onClick={() => navigate('/admin/events')}
                            sx={{ borderRadius: '20px', textTransform: 'none', fontWeight: 700, color: DS.textMuted }}
                        >
                            Back to events
                        </Button>
                    }
                />

                {/* Form sections */}
                <Grid item xs={12} md={8}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <FormSection
                            sectionKey="general"
                            step={1}
                            title="Event details"
                            subtitle="Code, name and when the event runs"
                            complete={generalComplete}
                            expanded={!!expandedSections.general}
                            onToggle={handleSectionToggle}
                            delay={0.05}
                        >
                            <EventGeneralDetailsForm
                                formData={formData}
                                isEditMode={isEditMode}
                                handleInputChange={handleInputChange}
                                onValidationChange={handleFormValidationChange}
                                handleCheckboxChange={handleCheckboxChange}
                                handleCustomBackgroundChange={handleCustomBackgroundChange}
                            />
                        </FormSection>

                        <FormSection
                            sectionKey="access"
                            step={2}
                            title="Access"
                            subtitle="Who can play: anyone with the code, or invited players only"
                            complete
                            expanded={!!expandedSections.access}
                            onToggle={handleSectionToggle}
                            delay={0.075}
                        >
                            <EventAccessDetailsForm
                                formData={formData}
                                onAccessChange={handleAccessChange}
                                onInvitedPlayersChange={handleInvitedPlayersChange}
                            />
                        </FormSection>

                        <FormSection
                            sectionKey="rewards"
                            step={3}
                            title="Rewards"
                            subtitle="What players get for taking part"
                            complete={rewardComplete}
                            expanded={!!expandedSections.rewards}
                            onToggle={handleSectionToggle}
                            delay={0.1}
                        >
                            <EventRewardsForm
                                formData={formData}
                                handleInputChange={handleInputChange}
                                onValidationChange={handleFormValidationChange}
                            />
                        </FormSection>

                        <FormSection
                            sectionKey="rounds"
                            step={4}
                            title="Rounds"
                            subtitle="Courses, disc bags and round locking"
                            complete={totalRounds > 0 && configuredRounds === totalRounds}
                            expanded={!!expandedSections.rounds}
                            onToggle={handleSectionToggle}
                            delay={0.15}
                        >
                            <EventRoundDetailsForm
                                formData={formData}
                                handleRoundChange={handleRoundChange}
                                handleInputChange={handleInputChange}
                                onValidationChange={handleFormValidationChange}
                            />
                        </FormSection>
                    </Box>
                </Grid>

                {/* Summary + submit */}
                <Grid item xs={12} md={4}>
                    <Box sx={{
                        position: { md: 'sticky' },
                        top: { md: 16 },
                        background: DS.surface,
                        border: `1px solid ${DS.border}`,
                        borderRadius: DS.radius,
                        p: { xs: 2, sm: 2.5 },
                        ...enterSx(0.2),
                    }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 1 }}>
                            <Overline>Setup progress</Overline>
                            <Typography sx={{ fontSize: '13px', fontWeight: 700, color: DS.text, ...numericSx }}>
                                {Math.round(progress)}%
                            </Typography>
                        </Box>
                        <Meter value={progress / 100} color={DS.brand} height={6} />

                        <Box sx={{ mt: 2, mx: -1 }}>
                            {checklist.map((item) => (
                                <Box
                                    key={item.key}
                                    onClick={() => openSection(item.key)}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1.25,
                                        px: 1,
                                        py: 0.75,
                                        borderRadius: '8px',
                                        cursor: 'pointer',
                                        '&:hover': { background: DS.inset },
                                    }}
                                >
                                    {item.done
                                        ? <CheckCircleIcon sx={{ fontSize: 18, color: DS.brand }} />
                                        : <RadioButtonUncheckedIcon sx={{ fontSize: 18, color: DS.textFaint }} />}
                                    <Box sx={{ minWidth: 0 }}>
                                        <Typography sx={{ fontSize: '13px', fontWeight: 700, color: DS.text }}>{item.label}</Typography>
                                        <Typography noWrap sx={{ fontSize: '12px', color: DS.textMuted }}>{item.detail}</Typography>
                                    </Box>
                                </Box>
                            ))}
                        </Box>

                        <Box sx={{ mt: 2, pt: 1.5, borderTop: `1px solid ${DS.border}` }}>
                            <Overline>Summary</Overline>
                            <SummaryRow label="Code">
                                <Box component="span" sx={{ fontFamily: 'monospace' }}>{formData.eventID || '—'}</Box>
                            </SummaryRow>
                            <SummaryRow label="Name">{formData.eventName || '—'}</SummaryRow>
                            <SummaryRow label="Starts (UTC)">{start?.isValid() ? start.format('D MMM YYYY HH:mm') : '—'}</SummaryRow>
                            <SummaryRow label="Ends (UTC)">{end?.isValid() ? end.format('D MMM YYYY HH:mm') : '—'}</SummaryRow>
                            <SummaryRow label="Duration">{durationLabel}</SummaryRow>
                            <SummaryRow label="Access">{accessLabel}</SummaryRow>
                            <SummaryRow label="Custom background">{formData.banner === EventBanner.CustomBackground ? 'Yes' : 'No'}</SummaryRow>
                            <SummaryRow label="Event info">{formData.eventInfoURL?.trim() || '—'}</SummaryRow>
                            <SummaryRow label="Reward">{rewardLabel}</SummaryRow>
                            <SummaryRow label="Rounds">{totalRounds}</SummaryRow>
                        </Box>

                        {!isValid && (
                            <Box sx={{
                                mt: 2,
                                p: 1.25,
                                borderRadius: '10px',
                                background: alpha(DS.loss, 0.1),
                                border: `1px solid ${alpha(DS.loss, 0.3)}`,
                                color: DS.loss,
                                fontSize: '13px',
                                fontWeight: 600,
                            }}>
                                Some fields need fixing before you can {isEditMode ? 'update' : 'create'} the event.
                            </Box>
                        )}

                        <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            size="large"
                            disabled={!isValid}
                            startIcon={isEditMode ? <CheckCircleIcon /> : <AddIcon />}
                            sx={{ mt: 2, borderRadius: '24px', textTransform: 'none', fontWeight: 800, fontSize: '15px', py: 1.25 }}
                        >
                            {isEditMode ? 'Update Event' : 'Create Event'}
                        </Button>
                        <Typography sx={{ fontSize: '12px', color: DS.textFaint, mt: 1, textAlign: 'center' }}>
                            {isInvite
                                ? 'Saved events go to Pending for review. Invites are sent when it is made invite-only.'
                                : 'Saved events go to Pending for review.'}
                        </Typography>
                    </Box>
                </Grid>
            </Grid>

            {/* Dialog for success or error message */}
            <Dialog
                open={dialogOpen}
                onClose={handleDialogClose}
                maxWidth="xs"
                fullWidth
                PaperProps={{
                    sx: {
                        background: DS.surface,
                        border: `1px solid ${DS.border}`,
                        borderRadius: DS.radius,
                        backgroundImage: 'none',
                    },
                }}
            >
                <DialogTitle sx={{ textAlign: 'center', pt: 3 }}>
                    <CheckCircleIcon sx={{ fontSize: 40, color: DS.brand, display: 'block', mx: 'auto', mb: 1 }} />
                    <Typography component="span" sx={{ fontWeight: 800, fontSize: '20px', color: DS.text }}>
                        {isEditMode ? 'Event Updated Successfully!' : 'Event Added Successfully!'}
                    </Typography>
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ p: 1.5, borderRadius: '12px', background: DS.inset }}>
                        <SummaryRow label="Event name">{formData.eventName || 'N/A'}</SummaryRow>
                        <SummaryRow label="Event ID">
                            <Box component="span" sx={{ fontFamily: 'monospace' }}>{formData.eventID}</Box>
                        </SummaryRow>
                        <SummaryRow label="Number of rounds">{formData.rounds.length}</SummaryRow>
                        <SummaryRow label="Event access">{accessLabel}</SummaryRow>
                    </Box>
                    <Typography sx={{ fontSize: '13px', color: DS.textMuted, textAlign: 'center', mt: 2 }}>
                        You can now manage this event in the event list.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
                    <Button
                        onClick={handleDialogClose}
                        variant="contained"
                        color="primary"
                        sx={{ borderRadius: '20px', textTransform: 'none', fontWeight: 700, px: 4 }}
                    >
                        Go to Event List
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default AddEventForm;
